/**
 * The cards of the room's players, as the server lets THIS viewer see them (owner 7.10.2026, assignment §6–7): fetched once per
 * room and kept, preloaded when a Line opens so the target's five seconds never start on an empty card. A room change empties
 * the cache (the right to see a card is the room's); a block or a privacy change is the server's to apply on the next fetch, so
 * a card older than a minute is fetched again when opened.
 */
import { signal } from '@preact/signals';
import type { PlayerCardView } from '@snw/protocol';
import { playerCard as fetchCard } from '../net/api';

type Entry = { card: PlayerCardView | null; at: number; error?: string };
const STALE_MS = 60_000;
let roomId: string | null = null;
const cache = new Map<string, Entry>();
/** bumps when a card arrives so an open card re-renders */
export const cardsVersion = signal(0);

export function cardsForRoom(id: string | null): void { if (id !== roomId) { roomId = id; cache.clear(); cardsVersion.value++; } }
export function cardOf(playerId: string): Entry | undefined { return cache.get(playerId); }
export async function loadCard(playerId: string, force = false): Promise<PlayerCardView | null> {
  const have = cache.get(playerId); const now = Date.now();
  if (have && !force && (have.card ? now - have.at < STALE_MS : now - have.at < 5_000)) return have.card;
  try { const card = await fetchCard(playerId, roomId ?? undefined); cache.set(playerId, { card, at: now }); cardsVersion.value++; return card; }
  catch (e) { cache.set(playerId, { card: null, at: now, error: (e as { code?: string }).code ?? 'FAILED' }); cardsVersion.value++; return null; }
}
/** the whole table at once, before the target turns begin */
export function preloadCards(playerIds: string[]): void { for (const id of playerIds) void loadCard(id); }
/** /views: a recorded player's card as a fixture, never fetched (the clock stands still there, so it never goes stale) */
export function freezeCard(card: PlayerCardView, at: number): void { cache.set(card.id, { card, at }); cardsVersion.value++; }
